import { FC } from 'react';
import * as yup from 'yup';
import Notify from '../Common/Notify';
import FormError from '../Common/FormError';
import { Form, Field } from 'react-final-form';
import { validateFormValues } from '../../helpers';
import LoadingIndicator from '../Common/LoadingIndicator';
import { useCreateServiceProvider } from '../../hooks/complete_profile/useCreateServiceProvider';
import useFetchCountries from '../../hooks/useFetchCountries';

const categories = [
  { id: 0, name: 'Lawyers' },
  { id: 1, name: 'Insurance' },
  { id: 2, name: 'Background Investors' },
  { id: 3, name: 'Home Appraisal Inspectors' },
  { id: 4, name: 'Local Services' },
];

const CreateProviderProfile: FC = () => {
  const { countries, provinces, handleSelectCountry } = useFetchCountries();

  const validationSchema = yup.object({
    name: yup.string().required('Company name is required'),
    rate: yup.number().required('Hourly rate is required'),
    category: yup.string().required('Category is required'),
    state: yup.string().required('State is required'),
    country: yup.string().required('Country is required'),
    city: yup.string().required('City is required'),
    address: yup.string().required('Address is required'),
    description: yup.string().required('Description / bio is required'),
  });

  const validate = validateFormValues(validationSchema);
  const { onSubmitHandler, submitting, error } = useCreateServiceProvider();

  return (
    <div>
      <h1 className='fw-700 h0 mb-10'>Let's Know More About You</h1>
      <Form
        onSubmit={onSubmitHandler}
        validate={validate}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <div>{error && <Notify type='danger' message={error} />}</div>

            <div className='mb-5 col-lg-12'>
              <Field name='name' type='textarea'>
                {({ input, meta }) => (
                  <>
                    <label className='form-label text-capitalize mt-3'>company name</label>
                    <div className='mb-3'>
                      <input type='text' {...input} className='form-control form-control-lg form-control-solid' />
                      <FormError meta={meta} />
                    </div>
                  </>
                )}
              </Field>
            </div>

            <div className='mb-5 col-lg-12'>
              <Field name='description' type='textarea'>
                {({ input, meta }) => (
                  <>
                    <label className='form-label text-capitalize mt-3'>description</label>
                    <div className='mb-3'>
                      <input type='text' {...input} className='form-control form-control-lg form-control-solid' />
                      <FormError meta={meta} />
                    </div>
                  </>
                )}
              </Field>
            </div>

            <div className='mb-5 col-lg-12'>
              <Field name='logo' type='file'>
                {({ input: { onChange, value, ...input }, meta }) => (
                  <>
                    <label className='form-label text-capitalize mt-3'>logo</label>
                    <div className='mb-3'>
                      <input type='file' {...input} onChange={({ target }) => onChange(target.files)} className='form-control form-control-lg form-control-solid' />
                      <FormError meta={meta} />
                    </div>
                  </>
                )}
              </Field>
            </div>

            <div className='row'>
              <div className='mb-5 col-lg-6'>
                <Field name='rate' type='number'>
                  {({ input, meta }) => (
                    <>
                      <label className='form-label text-capitalize mt-3'>Hourly rate</label>
                      <div className='mb-3'>
                        <input type='number' {...input} placeholder='in NGN' className='form-control form-control-lg form-control-solid' />
                        <FormError meta={meta} />
                      </div>
                    </>
                  )}
                </Field>
              </div>

              <div className='mb-5 col-lg-6'>
                <Field name='category' type='select'>
                  {({ input, meta }) => (
                    <>
                      <label className='form-label text-capitalize mt-3'>Category</label>
                      <div className='mb-3'>
                        <select {...input} className='form-select form-select-lg form-select-solid'>
                          <option value='' defaultValue=''>
                            Category
                          </option>
                          {categories.map((category) => {
                            return (
                              <option key={category.id} value={category.id}>
                                {category.name}
                              </option>
                            );
                          })}
                        </select>
                        <FormError meta={meta} />
                      </div>
                    </>
                  )}
                </Field>
              </div>
            </div>

            <label className='form-label text-capitalize mt-3'>Tell us your location</label>
            <div className='mb-5 col-lg-12'>
              <Field name='address' type='textarea'>
                {({ input, meta }) => (
                  <>
                    <div className='mb-3'>
                      <input type='text' {...input} placeholder='Address' className='form-control form-control-lg form-control-solid' />
                      <FormError meta={meta} />
                    </div>
                  </>
                )}
              </Field>
            </div>

            <div className='mb-5 col-lg-12'>
              <Field name='city' type='text'>
                {({ input, meta }) => (
                  <>
                    <div className='mb-3'>
                      <input type='text' placeholder='City' {...input} className='form-control form-control-lg form-control-solid' />
                      <FormError meta={meta} />
                    </div>
                  </>
                )}
              </Field>
            </div>

            <div className='row'>
              <div className='mb-5 col-lg-6'>
                <Field name='country' type='select'>
                  {({ input, meta }) => (
                    <>
                      <div className='mb-3'>
                        <select
                          {...input}
                          className='form-select form-select-lg form-select-solid'
                          onChange={(e) => {
                            input.onChange(e);
                            handleSelectCountry(e);
                          }}>
                          <option value=''>Country</option>
                          {countries.length &&
                            countries.map((country) => {
                              return (
                                <option key={country.id} value={country.id}>
                                  {country.name}
                                </option>
                              );
                            })}
                        </select>
                        <FormError meta={meta} />
                      </div>
                    </>
                  )}
                </Field>
              </div>
              <div className='mb-5 col-lg-6'>
                <Field name='state' type='select'>
                  {({ input, meta }) => (
                    <>
                      <div className='mb-3'>
                        <select {...input} className='form-select form-select-lg form-select-solid'>
                          <option value='' defaultValue=''>
                            State/Province
                          </option>
                          {provinces.map((state) => {
                            return (
                              <option key={state.id} value={state.id}>
                                {state.name}
                              </option>
                            );
                          })}
                        </select>
                        <FormError meta={meta} />
                      </div>
                    </>
                  )}
                </Field>
              </div>
            </div>

            <div className='d-flex flex-stack pt-15'>
              <button type='submit' disabled={submitting} className='btn btn-lg btn-primary me-3'>
                {submitting ? <LoadingIndicator /> : 'Continue'}
              </button>
            </div>
          </form>
        )}
      />
    </div>
  );
};

export default CreateProviderProfile;
